// Gabruz Universe — fetch-based API client.
// Talks to the Express/SQL backend in /server.
// Auth: JWT stored in localStorage, sent as Authorization: Bearer <token>.

const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:5000/api/v1";

const TOKEN_KEY = "gabruz-token";
const USER_KEY  = "gabruz-user";

// ============= Token + user helpers =============
export function getToken() { return localStorage.getItem(TOKEN_KEY); }
export function setToken(t) { localStorage.setItem(TOKEN_KEY, t); }
export function clearToken() { localStorage.removeItem(TOKEN_KEY); }

export function getCurrentUser() {
  const raw = localStorage.getItem(USER_KEY);
  return raw ? JSON.parse(raw) : null;
}
export function setCurrentUser(user) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}
export function clearCurrentUser() {
  localStorage.removeItem(USER_KEY);
}

export function getUser() {
  return getCurrentUser();
}
export function saveState(state) {
  // No persistent client-side state save needed for this demo.
  return state;
}

// ============= Low-level fetch wrapper =============
// §31 — Errors thrown from here are surfaced to users as toasts, so the
// message must read like English, not like a stack trace. Three sources
// of failure get translated:
//   - network outage / API down  → "Couldn't reach the server…"
//   - 5xx                        → "Server hit an unexpected error…"
//   - everything else            → server's own data.error (already friendly)
async function request(method, path, body) {
  const headers = { "Content-Type": "application/json" };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  // Cache-bust every GET with a timestamp query param. Belt-and-suspenders
  // on top of Cache-Control: no-store — some edge caches (Azure Front
  // Door, Cloudflare, corporate proxies) strip / ignore cache headers,
  // but the unique URL guarantees they can never serve a hit. Non-GET
  // requests don't need this since they're not cached anyway.
  let finalPath = path;
  if (method === "GET") {
    const sep = path.includes("?") ? "&" : "?";
    finalPath = `${path}${sep}_ts=${Date.now()}`;
  }

  let res;
  try {
    res = await fetch(`${API_BASE}${finalPath}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      // Force the browser to bypass its HTTP cache. Without this, GETs
      // without explicit server cache directives fall into heuristic
      // caching — the useAutoReload poll would then keep returning the
      // same stale response even after another device updated the DB.
      cache: "no-store"
    });
  } catch (netErr) {
    const err = new Error("Couldn't reach the server. Check your connection and try again.");
    err.status = 0;
    err.cause  = netErr;
    throw err;
  }

  // Auto-logout on auth failure so the UI can redirect to Login
  if (res.status === 401) {
    clearToken();
    clearCurrentUser();
  }

  const text = await res.text();
  const data = text ? safeJson(text) : null;

  if (!res.ok) {
    const serverMsg = data?.error;
    const msg = serverMsg
      || (res.status >= 500
        ? "The server hit an unexpected error. Please try again in a moment."
        : res.statusText || `Request failed (${res.status})`);
    const err = new Error(msg);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
}

function safeJson(s) { try { return JSON.parse(s); } catch { return s; } }

const get   = (p)     => request("GET",    p);
const post  = (p, b)  => request("POST",   p, b);
const put   = (p, b)  => request("PUT",    p, b);
const patch = (p, b)  => request("PATCH",  p, b);
const del   = (p)     => request("DELETE", p);

// ============= Auth =============
export async function login(email, password) {
  const { token, user } = await post("/auth/login", { email, password });
  setToken(token);
  setCurrentUser(user);
  return { user, token };
}

// Public — used by the login screen quick-pick buttons.
export async function listDemoUsers() {
  return get("/auth/demo-users");
}

export function logout() {
  clearToken();
  clearCurrentUser();
}

// PUT /auth/profile — self-service profile update.
// Body: { firstName?, lastName?, displayName?, phone?, email?,
//         oldPassword?, newPassword? }
// If the email changes the server returns a fresh JWT we rotate into
// local storage so subsequent requests don't 401 on stale claims.
export async function updateMyProfile(body) {
  const { user, token } = await put("/auth/profile", body);
  if (token) setToken(token);
  if (user)  setCurrentUser(user);
  return user;
}

// ============= Aggregate load =============
// Fetches everything the views need in parallel and shapes it into the
// flat object the existing components consume: { members, groups, teams,
// matches, availability, squads, announcements, transactions, notifications }.
// Endpoints the user lacks permission for (e.g. transactions for a PLAYER)
// are tolerated — they return [] instead of throwing.
export async function loadState() {
  async function tolerant(path, fallback = []) {
    try { return await get(path); }
    catch (e) { if (e.status === 403) return fallback; throw e; }
  }

  const [
    members, groups, teams, matches, availability,
    squads, announcements, transactions, notifications,
    flyerTemplates
  ] = await Promise.all([
    tolerant("/members"),
    tolerant("/groups"),
    tolerant("/teams"),
    tolerant("/matches"),
    tolerant("/availability"),
    tolerant("/squads"),
    tolerant("/announcements"),
    tolerant("/transactions"),
    tolerant("/notifications"),
    tolerant("/flyer-templates")
  ]);

  return {
    members, groups, teams, matches, availability,
    squads, announcements, transactions, notifications,
    flyerTemplates
  };
}

// Kept as a backwards-compatible no-op so any leftover import works.
export function resetState() {
  // Backend has no reset endpoint by design (use `npm run db:reset` on the server).
  return loadState();
}

// ============= Per-resource API =============
// Multipart upload helper for the Excel schedule preview.
export async function previewSchedule(file) {
  const token = getToken();
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${API_BASE}/matches/upload`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form
  });
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    const err = new Error(data?.error || res.statusText);
    err.status = res.status;
    throw err;
  }
  return data;
}

export async function applySchedule(token, mode) {
  return request("POST", `/matches/upload/apply`, { token, mode });
}

// Authenticated file download — the endpoint requires a Bearer token, so a
// plain <a href> won't work. Fetch + Blob + synthetic <a download> click.
// League match schedule — mirrors the matches/upload helpers but posts
// to the league.Matches endpoints and downloads a league-specific template.
export async function previewLeagueSchedule(file) {
  const token = getToken();
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${API_BASE}/league/matches/upload`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form
  });
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    const err = new Error(data?.error || res.statusText);
    err.status = res.status;
    throw err;
  }
  return data;
}
export async function applyLeagueSchedule(token, mode) {
  return request("POST", `/league/matches/upload/apply`, { token, mode });
}
export async function downloadLeagueScheduleTemplate() {
  const t = getToken();
  const res = await fetch(`${API_BASE}/league/matches/upload/template`, {
    headers: t ? { Authorization: `Bearer ${t}` } : {}
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = "league-schedule-template.xlsx";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Umpire bulk upload — mirrors the schedule upload helpers.
export async function previewUmpireUpload(file) {
  const token = getToken();
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${API_BASE}/league/umpires/upload`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form
  });
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    const err = new Error(data?.error || res.statusText);
    err.status = res.status;
    throw err;
  }
  return data;
}
export async function applyUmpireUpload(token) {
  return request("POST", `/league/umpires/upload/apply`, { token });
}
export async function downloadUmpireTemplate() {
  const t = getToken();
  const res = await fetch(`${API_BASE}/league/umpires/upload/template`, {
    headers: t ? { Authorization: `Bearer ${t}` } : {}
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = "umpires-template.xlsx";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function downloadScheduleTemplate() {
  const t = getToken();
  const res = await fetch(`${API_BASE}/matches/upload/template`, {
    headers: t ? { Authorization: `Bearer ${t}` } : {}
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = "schedule-template.xlsx";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export const api = {
  // members
  listMembers:   ()        => get("/members"),
  listInactiveMembers: () => get("/members/inactive"),
  reactivateMember: (id)  => patch(`/members/${id}/reactivate`),
  createMember:  (m)       => post("/members", m),
  updateMember:  (id, m)   => put(`/members/${id}`, m),
  deleteMember:  (id)      => del(`/members/${id}`),

  // groups
  listGroups:    ()        => get("/groups"),
  createGroup:   (g)       => post("/groups", g),
  updateGroup:   (id, g)   => put(`/groups/${id}`, g),
  deleteGroup:   (id)      => del(`/groups/${id}`),
  listGroupMembers:  (id)              => get(`/groups/${id}/members`),
  addGroupMember:    (id, body)        => post(`/groups/${id}/members`, body),
  removeGroupMember: (id, userId)      => del(`/groups/${id}/members/${userId}`),
  listGroupMessages: (id)              => get(`/groups/${id}/messages`),
  postGroupMessage:  (id, body)        => post(`/groups/${id}/messages`, { body }),

  // teams
  listTeams:     ()        => get("/teams"),
  createTeam:    (t)       => post("/teams", t),
  updateTeam:    (id, t)   => put(`/teams/${id}`, t),
  deleteTeam:    (id)      => del(`/teams/${id}`),

  // matches
  listMatches:   ()        => get("/matches"),
  createMatch:   (m)       => post("/matches", m),
  updateMatch:   (id, m)   => put(`/matches/${id}`, m),
  deleteMatch:   (id)      => del(`/matches/${id}`),
  previewSchedule,
  applySchedule,
  downloadScheduleTemplate,

  // availability
  listAvailability: ()                   => get("/availability"),
  forMatch:         (matchId)            => get(`/availability/${matchId}`),
  setAvailability:  (matchId, userId, body) => put(`/availability/${matchId}/${userId}`, body),

  // squads
  listSquads:    ()        => get("/squads"),
  createSquad:   (s)       => post("/squads", s),
  updateSquad:   (id, s)   => put(`/squads/${id}`, s),
  deleteSquad:   (id)      => del(`/squads/${id}`),

  // announcements
  listAnnouncements: ()         => get("/announcements"),
  createAnnouncement: (a)       => post("/announcements", a),
  updateAnnouncement: (id, a)   => put(`/announcements/${id}`, a),
  deleteAnnouncement: (id)      => del(`/announcements/${id}`),

  // transactions
  listTransactions:  (teamId)   => get(`/transactions${teamId ? `?teamId=${teamId}` : ""}`),
  listTeamFinanceSummary: ()    => get("/transactions/by-team"),
  createTransaction: (t)        => post("/transactions", t),
  deleteTransaction: (id)       => del(`/transactions/${id}`),
  financeSummary:    ()         => get("/transactions/summary"),

  // team fees — master config + per-player collection
  getTeamFeeConfig:    (teamId, year) => get(`/team-fees/config?teamId=${teamId}&year=${year}`),
  saveTeamFeeConfig:   (cfg)          => put("/team-fees/config", cfg),
  listPlayerFees:      (teamId, year) => get(`/team-fees/players?teamId=${teamId}&year=${year}`),
  savePlayerFee:       (row)          => put("/team-fees/players", row),

  // direct messages — cross-team chat
  listMessageThreads:  ()             => get("/messages/threads"),
  getMessagesWith:     (userId)       => get(`/messages/with/${userId}`),
  unreadMessageCount:  ()             => get("/messages/unread"),
  sendMessage:         (toUserId, body) => post("/messages", { toUserId, body }),
  // group chat rooms
  createChatRoom:      (name, memberIds) => post("/messages/rooms", { name, memberIds }),
  getChatRoom:         (roomId)       => get(`/messages/rooms/${roomId}`),
  sendRoomMessage:     (roomId, body) => post(`/messages/rooms/${roomId}/messages`, { body }),
  // delete own messages
  deleteMessage:       (id)           => del(`/messages/${id}`),
  deleteRoomMessage:   (roomId, id)   => del(`/messages/rooms/${roomId}/messages/${id}`),
  // group member management
  addRoomMembers:      (roomId, userIds) => post(`/messages/rooms/${roomId}/members`, { userIds }),
  removeRoomMember:    (roomId, userId)  => del(`/messages/rooms/${roomId}/members/${userId}`),

  // Phase 4 #1 — match results & top performers
  getMatchResult:     (matchId)          => get(`/matches/${matchId}/result`),
  saveMatchResult:    (matchId, body)    => put(`/matches/${matchId}/result`, body),
  listRecentResults:  ({ limit = 5 } = {}) => get(`/matches/results/recent?limit=${limit}`),

  // weather forecast (proxies OpenWeatherMap on the server, hides API key)
  weatherStatus:       ()                            => get("/weather/status"),
  weatherForecast:     ({ venue, date, time })       => get(
    `/weather/forecast?venue=${encodeURIComponent(venue)}` +
    (date ? `&date=${encodeURIComponent(date)}` : "") +
    (time ? `&time=${encodeURIComponent(time)}` : "")
  ),

  // notifications
  listNotifications: ()         => get("/notifications"),
  createNotification:(n)        => post("/notifications", n),
  markRead:          (id)       => put(`/notifications/${id}/read`),

  // practice & development (Phase 3 — Rowdys Cricket scoped in the UI)
  listPracticeSessions:   (teamId) => get(`/practice/sessions${teamId ? `?teamId=${teamId}` : ""}`),
  createPracticeSession:  (body)   => post("/practice/sessions", body),
  updatePracticeSession:  (id, b)  => put(`/practice/sessions/${id}`, b),
  deletePracticeSession:  (id)     => del(`/practice/sessions/${id}`),

  listPracticeAttendance: (teamId) => get(`/practice/attendance${teamId ? `?teamId=${teamId}` : ""}`),
  setPracticeAttendance:  (sessionId, userId, body) => put(`/practice/attendance/${sessionId}/${userId}`, body),

  listDrills:    (teamId) => get(`/practice/drills${teamId ? `?teamId=${teamId}` : ""}`),
  createDrill:   (body)   => post("/practice/drills", body),
  updateDrill:   (id, b)  => put(`/practice/drills/${id}`, b),
  deleteDrill:   (id)     => del(`/practice/drills/${id}`),

  listGoals:     (teamId) => get(`/practice/goals${teamId ? `?teamId=${teamId}` : ""}`),
  createGoal:    (body)   => post("/practice/goals", body),
  updateGoal:    (id, b)  => put(`/practice/goals/${id}`, b),
  deleteGoal:    (id)     => del(`/practice/goals/${id}`),

  listSkillRatings:   (teamId) => get(`/practice/skill-ratings${teamId ? `?teamId=${teamId}` : ""}`),
  createSkillRating:  (body)   => post("/practice/skill-ratings", body),
  deleteSkillRating:  (id)     => del(`/practice/skill-ratings/${id}`),

  // Phase 3 — cricket player career stats (per team, per player).
  listPlayerStats: (teamId)             => get(`/player-stats?teamId=${teamId}`),
  getPlayerStats:  (userId, teamId)     => get(`/player-stats/${userId}?teamId=${teamId}`),
  savePlayerStats: (userId, body)       => put(`/player-stats/${userId}`, body),

  // Phase 3 — admin-managed custom cricket stat fields.
  listCricketStatFields:  ()       => get("/cricket-stat-fields"),
  createCricketStatField: (body)   => post("/cricket-stat-fields", body),
  updateCricketStatField: (id, b)  => put(`/cricket-stat-fields/${id}`, b),
  deleteCricketStatField: (id)     => del(`/cricket-stat-fields/${id}`),

  // Phase 3 — per-team flyer template designer.
  getFlyerTemplate:  (teamId)       => get(`/flyer-templates/${teamId}`),
  saveFlyerTemplate: (teamId, body) => put(`/flyer-templates/${teamId}`, body),

  // Phase 3 — per-team uploaded logo (base64 data URL).
  uploadTeamLogo: (teamId, dataUrl) => put(`/teams/${teamId}/logo`, { logoUrl: dataUrl }),

  // Phase 4 — per-match cricket performance entries that auto-roll into
  // career stats. See server/src/routes/matchPerformance.js.
  listMatchPerformance: (matchId)         => get(`/match-performance?matchId=${matchId}`),
  getUserMatchHistory:  (userId)          => get(`/match-performance/user/${userId}`),
  getUserMatchTotals:   (userId)          => get(`/match-performance/totals/${userId}`),
  saveMatchPerformance: (matchId, rows)   => put("/match-performance/bulk", { matchId, rows }),
  deleteMatchPerformance: (perfId)        => del(`/match-performance/${perfId}`),

  // Phase 4 — Splits (Splitwise-style personal shared expenses).
  // Access is scoped to payer + participants; there's no admin bulk list.
  listMySplits:       ()                            => get("/splits/mine"),
  getSplit:           (id)                          => get(`/splits/${id}`),
  createSplit:        (body)                        => post("/splits", body),
  markSplitPaid:      (id, userId)                  => patch(`/splits/${id}/participants/${userId}/paid`),
  markSplitUnpaid:    (id, userId)                  => patch(`/splits/${id}/participants/${userId}/unpaid`),
  closeSplit:         (id)                          => patch(`/splits/${id}/close`),
  deleteSplit:        (id)                          => del(`/splits/${id}`),
  remindSplitAll:     (id)                          => post(`/splits/${id}/remind`),
  remindSplitOne:     (id, userId)                  => post(`/splits/${id}/remind/${userId}`),

  // Receipt OCR — POST a base64 data URL of the receipt photo. The
  // server hands it to Azure Document Intelligence (prebuilt-receipt)
  // and returns { merchant, transactionDate, items, subtotal, tax,
  // tip, total }. Every field is best-effort; the UI must let the
  // user edit anything.
  scanReceipt:        (dataUrl)                     => post("/receipts/scan", { image: dataUrl }),

  // Phase 4 — Public registration (self-service signup, admin approval).
  // register() is intentionally public — no auth token attached. The
  // request helper skips the Authorization header when no token exists.
  // Phase 4 — Member avatars. Compressed client-side to ~10KB WebP;
  // stored in core.UserAvatars. See src/utils/avatar.js + Avatar.jsx.
  uploadAvatar:             (id, dataUrl) => put(`/members/${id}/avatar`, { dataUrl }),
  deleteAvatar:             (id)          => del(`/members/${id}/avatar`),
  fetchAvatarBlob:          async (id) => {
    // Custom fetch to grab the raw bytes with Authorization attached
    // (the <img> tag can't do that on its own).
    const token = getToken();
    const res = await fetch(`${API_BASE}/members/${id}/avatar`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      cache: "no-store"
    });
    if (!res.ok) throw new Error(`Avatar fetch failed (${res.status})`);
    return res.blob();
  },

  // Phase 5 — Pittsburgh Cricket League / Executive Committee.
  //
  // Announcements were unified into /announcements with an
  // `isLeagueOnly` flag (see createAnnouncement); the old League-only
  // endpoints have been removed.
  listCommittee:            ()      => get("/league/committee"),

  // Phase 5 Part 3 — Team Registration.
  listRegisteredTeams:      ()          => get("/league/teams"),
  listTeamOptions:          ()          => get("/league/teams/options"),
  registerLeagueTeam:       (body)      => post("/league/teams", body),
  updateRegisteredTeam:     (id, body)  => put(`/league/teams/${id}`, body),
  setRegisteredTeamStatus:  (id, status) => patch(`/league/teams/${id}/status`, { status }),
  deleteRegisteredTeam:     (id)        => del(`/league/teams/${id}`),
  setTeamDivision:          (id, divisionId) =>
    patch(`/league/teams/${id}/division`, { divisionId }),

  // Divisions.
  listDivisions:            ()          => get("/league/divisions"),
  createDivision:           (d)         => post("/league/divisions", d),
  updateDivision:           (id, d)     => put(`/league/divisions/${id}`, d),
  deleteDivision:           (id)        => del(`/league/divisions/${id}`),
  reorderDivisions:         (ids)       => patch("/league/divisions/reorder", { ids }),

  // Finances — seasons, dashboards, ledgers.
  listFinanceSeasons:       ()          => get("/league/finance/seasons"),
  createFinanceSeason:      (body)      => post("/league/finance/seasons", body),
  activateFinanceSeason:    (id)        => patch(`/league/finance/seasons/${id}/activate`),
  getFinanceSummary:        (seasonId)  => get(`/league/finance/summary?seasonId=${seasonId}`),

  listTeamFees:             (seasonId)  => get(`/league/finance/team-fees?seasonId=${seasonId}`),
  createTeamFee:            (body)      => post("/league/finance/team-fees", body),
  updateTeamFee:            (id, body)  => put(`/league/finance/team-fees/${id}`, body),
  deleteTeamFee:            (id)        => del(`/league/finance/team-fees/${id}`),

  listUmpiringPayments:     (seasonId)  => get(`/league/finance/umpiring?seasonId=${seasonId}`),
  createUmpiringPayment:    (body)      => post("/league/finance/umpiring", body),
  updateUmpiringPayment:    (id, body)  => put(`/league/finance/umpiring/${id}`, body),
  deleteUmpiringPayment:    (id)        => del(`/league/finance/umpiring/${id}`),
  listUmpireRates:          (seasonId)  => get(`/league/finance/umpire-rates?seasonId=${seasonId}`),
  createUmpireRate:         (body)      => post("/league/finance/umpire-rates", body),
  deleteUmpireRate:         (id)        => del(`/league/finance/umpire-rates/${id}`),

  listExpenses:             (seasonId)  => get(`/league/finance/expenses?seasonId=${seasonId}`),
  createExpense:            (body)      => post("/league/finance/expenses", body),
  updateExpense:            (id, body)  => put(`/league/finance/expenses/${id}`, body),
  deleteExpense:            (id)        => del(`/league/finance/expenses/${id}`),

  listCashAccounts:         (seasonId)  => get(`/league/finance/cash-accounts?seasonId=${seasonId}`),
  createCashAccount:        (body)      => post("/league/finance/cash-accounts", body),
  createCashSnapshot:       (body)      => post("/league/finance/cash-snapshots", body),

  // League match schedule (Excel-uploadable).
  listLeagueMatches:        ()          => get("/league/matches"),
  createLeagueMatch:        (m)         => post("/league/matches", m),
  updateLeagueMatch:        (id, m)     => put(`/league/matches/${id}`, m),
  deleteLeagueMatch:        (id)        => del(`/league/matches/${id}`),
  previewLeagueSchedule,
  applyLeagueSchedule,
  downloadLeagueScheduleTemplate,

  // Umpire pool.
  listUmpires:              ()          => get("/league/umpires"),
  createUmpire:             (u)         => post("/league/umpires", u),
  updateUmpire:             (id, u)     => put(`/league/umpires/${id}`, u),
  deleteUmpire:             (id)        => del(`/league/umpires/${id}`),
  previewUmpireUpload,
  applyUmpireUpload,
  downloadUmpireTemplate,

  // Umpire self-service — for users with UMPIRE role.
  listMyPolls:              ()          => get("/league/polls/mine"),
  selfNominate:             (matchId)   => post(`/league/polls/${matchId}/mine`),
  withdrawNomination:       (matchId)   => del(`/league/polls/${matchId}/mine`),

  // Umpire assignments — polls, nominations, and match-day picks.
  listUpcomingMatches:      ()          => get("/league/assignments/upcoming"),
  listAssignmentFlyer:      ()          => get("/league/assignments/flyer"),
  toggleMatchPoll:          (matchId, open) =>
    patch(`/league/matches/${matchId}/nominations`, { open: !!open }),
  listMatchNominations:     (matchId)   => get(`/league/matches/${matchId}/nominations`),
  addMatchNomination:       (matchId, umpireId) =>
    post(`/league/matches/${matchId}/nominations`, { umpireId }),
  removeMatchNomination:    (matchId, umpireId) =>
    del(`/league/matches/${matchId}/nominations/${umpireId}`),
  setMatchNominationStatus: (matchId, umpireId, status) =>
    patch(`/league/matches/${matchId}/nominations/${umpireId}`, { status }),
  setMatchLocked:           (matchId, locked) =>
    patch(`/league/matches/${matchId}/lock`, { locked: !!locked }),

  // Phase 5 Part 4 — Admin role management (team + league scope).
  listRoleAssignments:      ()      => get("/admin/roles"),
  assignRole:               (body)  => post("/admin/roles", body),
  unassignRole:             ({ userId, roleCode, teamId }) => {
    const qs = new URLSearchParams({ userId: String(userId), roleCode });
    if (teamId != null && teamId !== "") qs.set("teamId", String(teamId));
    return del(`/admin/roles?${qs.toString()}`);
  },

  listPublicTeams:          ()      => get("/registrations/teams"),
  register:                 (body)  => post("/registrations/register", body),
  listPendingRegistrations: ()      => get("/registrations/pending"),
  approveRegistration:      (id)    => patch(`/registrations/${id}/approve`),
  rejectRegistration:       (id, reason) => patch(`/registrations/${id}/reject`, { reason })
};
